// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Countdown to 31 August 2026
function Countdown() {
  const [diff, setDiff] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const target = new Date('2026-08-31T23:59:59');
    const tick = () => {
      const now = new Date();
      const secs = Math.max(0, Math.floor((target - now) / 1000));
      setDiff({
        days:    Math.floor(secs / 86400),
        hours:   Math.floor((secs % 86400) / 3600),
        minutes: Math.floor((secs % 3600) / 60),
        seconds: secs % 60,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex gap-3 justify-center mt-4" data-testid="countdown-timer">
      {[['Giorni', diff.days], ['Ore', diff.hours], ['Min', diff.minutes], ['Sec', diff.seconds]].map(([label, val]) => (
        <div key={label} className="text-center">
          <div className="text-2xl font-bold text-[#C9A84C] font-mono w-14 h-14 flex items-center justify-center rounded-lg" style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.2)' }}>
            {String(val).padStart(2, '0')}
          </div>
          <div className="text-[10px] text-[#888780] mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
}

const FAQ_ITEMS = [
  {
    q: 'Devo pagare subito?',
    a: 'No. Stripe non è ancora attivo. Iscriviti alla lista fondatori gratuitamente e ti avviseremo quando il pagamento sarà disponibile. Il tuo prezzo €4,99/mese sarà bloccato per sempre.'
  },
  {
    q: 'Cosa succede dopo il 31 agosto?',
    a: 'Il prezzo per i nuovi iscritti salirà a €6,99/mese. Chi si è iscritto prima mantiene €4,99/mese per sempre, senza eccezioni.'
  },
  {
    q: 'I miei dati sono al sicuro?',
    a: 'Sì. SAPIENS non condivide dati con terze parti e non ha pubblicità. I tuoi dati sono criptati e usati solo per migliorare la tua esperienza sulla piattaforma.'
  }
];

export default function PremiumPage() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('mensile'); // 'mensile' | 'annuale'
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [pricing, setPricing] = useState(null);
  // Deployment 12 — Art. 59 Cod. Cons. mandatory acceptance
  const [acceptArt59, setAcceptArt59] = useState(false);
  const [showArt59Error, setShowArt59Error] = useState(false);

  // Load dynamic pricing
  useEffect(() => {
    fetch(`${API_URL}/api/pricing/current`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setPricing(d))
      .catch(() => {});
  }, []);

  const monthlyPrice = pricing?.monthly_price ?? 4.99;
  const annualPrice = pricing?.annual_price ?? 39.0;
  const annualMonthly = pricing?.annual_monthly_equiv ?? 3.25;
  const phaseBadge = pricing?.badge ?? 'Fondatori';
  const phaseCta = pricing?.cta ?? 'Entra da Fondatore';

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!acceptArt59) {
      setShowArt59Error(true);
      return;
    }
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/premium-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true); // optimistic
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141412]" data-testid="premium-page">
      <Helmet>
        <title>SAPIENS Premium — Educazione Finanziaria Completa</title>
      </Helmet>

      {/* ── HERO ── */}
      <section className="pt-20 pb-12 text-center px-4">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-6" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#C9A84C' }}>
            Offerta Fondatori · Scade il 31 agosto 2026
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#E8E8E4] mb-5">SAPIENS Premium</h1>
          <p className="text-xl text-[#888780] max-w-2xl mx-auto leading-relaxed">
            Il tuo percorso verso la consapevolezza finanziaria completa.
          </p>
        </motion.div>
      </section>

      {/* ── PRICING BOX ── */}
      <section className="pb-16 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            style={{ background: 'linear-gradient(145deg, #1C1C1A, #222220)', border: '1.5px solid rgba(201,168,76,0.4)', borderRadius: 20, padding: '32px 28px' }}
            data-testid="pricing-box"
          >
            <div className="text-center mb-6">
              <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C' }}>
                {phaseBadge && `Offerta ${phaseBadge}`}{pricing?.valid_until ? ` — fino al ${new Date(pricing.valid_until).toLocaleDateString('it-IT', {day:'numeric',month:'long',year:'numeric'})}` : ''}
              </span>
            </div>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-1 mb-6 p-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
              {['mensile', 'annuale'].map(opt => (
                <button key={opt} onClick={() => setBilling(opt)}
                  className="flex-1 py-2 rounded-md text-sm font-medium transition-all capitalize"
                  style={{ background: billing === opt ? '#C9A84C' : 'transparent', color: billing === opt ? '#1C1C1A' : '#888780' }}
                  data-testid={`billing-${opt}`}>
                  {opt === 'mensile' ? 'Mensile' : 'Annuale'}
                </button>
              ))}
            </div>

            {/* Price display */}
            <AnimatePresence mode="wait">
              <motion.div key={billing} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="text-center mb-4">
                {billing === 'mensile' ? (
                  <>
                    <div className="text-5xl font-bold text-[#E8E8E4]" data-testid="price-mensile">€{monthlyPrice.toFixed(2).replace('.', ',')}<span className="text-xl font-normal text-[#888780]">/mese</span></div>
                    <div className="text-sm text-[#888780] mt-2">Prezzo {phaseBadge || 'corrente'}</div>
                  </>
                ) : (
                  <>
                    <div className="text-5xl font-bold text-[#E8E8E4]" data-testid="price-annuale">
                      €{annualPrice}<span className="text-xl font-normal text-[#888780]">/anno</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(61,122,82,0.2)', color: '#3D7A52' }}>Risparmia {pricing?.discount_pct ?? 35}%</span>
                      <span className="text-sm text-[#888780]">equivale a €{annualMonthly.toFixed(2).replace('.', ',')}/mese</span>
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Countdown — show only during fondatori phase */}
            {(!pricing?.phase || pricing.phase === 'fondatori') && <Countdown />}

            <div className="text-center mt-4 mb-6">
              <p className="text-xs text-[#888780]">Dal 1 settembre il prezzo salirà a <strong style={{ color: '#E8E8E4' }}>€6,99/mese</strong></p>
            </div>

            {/* Waitlist form */}
            {!submitted ? (
              <form onSubmit={handleWaitlist} className="space-y-3" data-testid="waitlist-form">
                <div className="text-center mb-2">
                  <div className="text-sm font-medium text-[#E8E8E4]">Iscriviti alla lista fondatori</div>
                  <div className="text-xs text-[#888780] mt-1">Ricevi Premium gratuito subito e mantieni €4,99/mese per sempre dopo settembre.</div>
                </div>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="la-tua@email.it"
                  required
                  className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-3 text-[#E8E8E4] placeholder-[#888780] focus:border-[#C9A84C]/50 outline-none"
                  data-testid="waitlist-email-input"
                />

                {/* Art. 59 Codice del Consumo — mandatory checkbox */}
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }} data-testid="checkout-art59-row">
                  <input
                    type="checkbox"
                    checked={acceptArt59}
                    onChange={e => { setAcceptArt59(e.target.checked); setShowArt59Error(false); }}
                    className="mt-1 w-4 h-4 accent-[#C9A84C] flex-shrink-0"
                    data-testid="checkout-checkbox-art59"
                    required
                  />
                  <span className="text-xs text-[#888780] leading-relaxed text-left">
                    Acconsento all'<strong className="text-[#E8E8E4]">esecuzione immediata</strong> della fornitura del contenuto digitale e dichiaro di essere consapevole della <strong className="text-[#E8E8E4]">perdita del diritto di recesso</strong> ex <strong className="text-[#E8E8E4]">art. 59 lett. o) D.Lgs. 206/2005</strong> (Codice del Consumo). Vedi <a href="/termini" className="text-[#C9A84C] underline">Art. 4 Termini di Servizio</a>.
                  </span>
                </label>
                {showArt59Error && !acceptArt59 && (
                  <p className="text-xs text-[#F08080]" data-testid="checkout-art59-error">⚠ Per procedere devi confermare la rinuncia al diritto di recesso ai sensi dell'art. 59 Cod. Cons.</p>
                )}

                <button type="submit" disabled={loading || !acceptArt59}
                  className="w-full py-3 rounded-lg font-semibold text-[#1C1C1A] transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ background: '#C9A84C' }}
                  data-testid="waitlist-submit-btn">
                  {loading ? 'Iscrizione...' : 'Iscriviti →'}
                </button>
                <p className="text-xs text-[#888780] text-center">
                  Nessun pagamento richiesto ora. Stripe non ancora attivo — ti avviseremo quando sarà possibile pagare.
                </p>
              </form>
            ) : (
              <div className="text-center py-4" data-testid="waitlist-success">
                <div className="text-2xl mb-2">🎉</div>
                <div className="text-[#E8E8E4] font-medium">Sei nella lista fondatori!</div>
                <div className="text-sm text-[#888780] mt-1">Ti contatteremo al lancio. Il tuo prezzo €4,99 è bloccato.</div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── COSA INCLUDE OGGI ── */}
      <section className="py-16 px-4" style={{ background: '#1C1C1A' }} data-testid="premium-features-today">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-medium text-[#E8E8E4] text-center mb-10">Cosa include Premium oggi</h2>
          <div className="space-y-3">
            {[
              'Livello 3 — Avanzato (3 lezioni)',
              'Livello 4 — Esperto (3 lezioni)',
              'Certificati PDF scaricabili',
              'Simulatore portafoglio educativo completo',
              'Archivio storico risultati calcolatori',
              'Report PDF riepilogo finanziario',
              'Aggiornamenti mensili esclusivi',
              'Accesso anticipato a nuovi contenuti',
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#C9A84C' }} />
                <span className="text-[#E8E8E4] text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMING SPRING 2027 ── */}
      <section className="py-16 px-4" style={{ background: '#141412' }} data-testid="premium-coming-soon">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C' }}>
              Prossimamente
            </div>
            <h2 className="text-2xl font-medium text-[#E8E8E4]">In arrivo primavera 2027</h2>
            <p className="text-sm text-[#888780] mt-2">Chi è Premium ora avrà accesso in anteprima.</p>
          </div>
          <div className="space-y-3">
            {[
              'Profilo Finanziario Personale completo',
              'Analisi portafoglio con dati reali',
              'Percorso di apprendimento personalizzato',
              'Calendario fiscale personale',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <span className="text-sm" style={{ color: '#C9A84C' }}>🔜</span>
                <span className="text-[#888780] text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── GARANZIA ── */}
      <section className="py-12 px-4" style={{ background: '#1C1C1A' }}>
        <div className="max-w-lg mx-auto">
          <div style={{ border: '1.5px solid rgba(61,122,82,0.4)', background: 'rgba(61,122,82,0.07)', borderRadius: 16, padding: '24px' }} className="text-center" data-testid="guarantee-box">
            <div className="text-3xl mb-3">🛡️</div>
            <div className="text-[#E8E8E4] font-medium mb-2">Soddisfatti o rimborsati entro 14 giorni.</div>
            <div className="text-sm text-[#888780]">Nessuna domanda. Cancella quando vuoi.</div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 px-4" style={{ background: '#141412' }} data-testid="premium-faq">
        <div className="max-w-xl mx-auto">
          <h2 className="text-xl font-medium text-[#E8E8E4] text-center mb-8">Domande frequenti</h2>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, overflow: 'hidden' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid={`faq-${i}`}>
                  <span className="text-[#E8E8E4] text-sm font-medium">{item.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-[#888780] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#888780] flex-shrink-0" />}
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} style={{ overflow: 'hidden' }}>
                      <div className="px-4 pb-4 text-sm text-[#888780] leading-relaxed">{item.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-16 px-4 text-center" style={{ background: '#1C1C1A' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-2xl font-medium text-[#E8E8E4] mb-3">Blocca il prezzo fondatori ora</h2>
          <p className="text-[#888780] mb-6 text-sm">€4,99/mese per sempre — solo fino al 31 agosto 2026</p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="px-8 py-3.5 rounded-lg font-semibold text-[#1C1C1A] text-base hover:opacity-90 transition-all"
            style={{ background: '#C9A84C' }}
            data-testid="premium-cta-scroll">
            Scopri SAPIENS Premium →
          </button>
        </motion.div>
      </section>
    </div>
  );
}
